import type { KartLoadout } from "../game/kartLoadout";
import type { DriverInput } from "../game/simulation/types";
import type { BattleEvent, BattleRules, BattleState } from "../game/battle/types";

export type { BattleEvent } from "../game/battle/types";

export type BattleRulesPatch = Partial<BattleRules> & { arenaId?: string; allowSoloPractice?: boolean };

/** The battle wire contract is intentionally separate from race protocol 9. */
export const BATTLE_PROTOCOL_VERSION = 2;
/** Session selectors are short opaque handles, not arbitrary payloads. */
export const BATTLE_SESSION_ID_MAX_LENGTH = 80;

export type BattleHelloAction = "create" | "join" | "reconnect";

/**
 * A Battle overlay is deliberately not a Battle participant.  It names an
 * already-existing session and receives detached snapshots only; it never
 * receives a participant binding or a reconnect credential.
 */
export interface BattleOverlayHelloMessage {
  type: "battle-overlay-hello";
  protocol: number;
  sessionId: string;
}

export interface BattleSessionSnapshotMessage {
  sessionId: string;
  matchId: string | null;
  phase: "lobby" | "countdown" | "active" | "results";
  tick: number;
  arenaId: string;
  rules: Readonly<BattleRules>;
  allowSoloPractice: boolean;
  hostParticipantId: string | null;
  participants: readonly {
    participantId: string;
    controller: "human" | "ai" | "twitch";
    name: string;
    loadout?: Partial<KartLoadout>;
    avatarLogin?: string;
    ready: boolean;
    connected: boolean;
    left: boolean;
    queued: boolean;
  }[];
  queuedParticipants: readonly {
    participantId: string;
    controller: "human" | "ai" | "twitch";
    name: string;
    loadout?: Partial<KartLoadout>;
    avatarLogin?: string;
    ready: boolean;
    connected: boolean;
    left: boolean;
    queued: boolean;
  }[];
  /** The renderer-load barrier for the current match, before the shared countdown. */
  loading: {
    pendingParticipantIds: readonly string[];
    deadlineAt: number;
  } | null;
  /** True only when the most recent load barrier timed out and was cancelled. */
  loadTimedOut: boolean;
  state: BattleState | null;
}

export type BattleClientMessage =
  | BattleOverlayHelloMessage
  | {
      type: "battle-hello";
      protocol: number;
      action: BattleHelloAction;
      sessionId?: string;
      participantId?: string;
      reconnectToken?: string;
      arenaId?: string;
      seed?: number;
      rules?: BattleRulesPatch;
      loadout?: Partial<KartLoadout>;
    }
  | { type: "battle-ready"; ready: boolean }
  | { type: "battle-load-ready"; matchId: string }
  | { type: "battle-configure"; patch: BattleRulesPatch }
  | { type: "battle-start" }
  | { type: "battle-rematch" }
  | { type: "battle-input"; matchId: string; seq: number; targetTick: number; input: DriverInput }
  | { type: "battle-autopilot"; enabled: boolean }
  | { type: "battle-leave" }
  | { type: "battle-lobby" }
  | { type: "battle-reset"; matchId: string };

export type BattleServerMessage =
  | {
      type: "battle-overlay-welcome";
      protocol: number;
      sessionId: string;
      tickRate: number;
      snapshotRate: number;
      serverTick: number;
      snapshot: BattleSessionSnapshotMessage;
    }
  | {
      type: "battle-overlay-snapshot";
      sessionId: string;
      tick: number;
      serverTime: number;
      snapshot: BattleSessionSnapshotMessage;
    }
  | {
      type: "battle-welcome";
      protocol: number;
      clientId: string;
      sessionId: string;
      participantId: string;
      reconnectToken: string;
      matchId: string | null;
      phase: BattleSessionSnapshotMessage["phase"];
      queued: boolean;
      tickRate: number;
      snapshotRate: number;
      serverTick: number;
      snapshot: BattleSessionSnapshotMessage;
    }
  | {
      type: "battle-snapshot";
      tick: number;
      serverTime: number;
      snapshot: BattleSessionSnapshotMessage;
    }
  | { type: "battle-event"; sessionId: string; matchId: string | null; event: BattleEvent }
  | { type: "battle-ack"; action: string; snapshot: BattleSessionSnapshotMessage }
  | { type: "battle-left"; sessionId: string; participantId: string }
  | { type: "battle-error"; code: string; message: string; fatal: boolean };

type RecordLike = Record<string, unknown>;
const isRecord = (value: unknown): value is RecordLike => typeof value === "object" && value !== null && !Array.isArray(value);
const finite = (value: unknown): value is number => typeof value === "number" && Number.isFinite(value);
const safeInteger = (value: unknown): value is number => typeof value === "number" && Number.isSafeInteger(value);
const stringValue = (value: unknown, max = 256): value is string => typeof value === "string" && value.length > 0 && value.length <= max;
const objectValue = (value: unknown): value is RecordLike => isRecord(value);

function hasOnly(value: RecordLike, keys: readonly string[]): boolean {
  const allowed = new Set(keys);
  return Object.keys(value).every(key => allowed.has(key));
}

function validInput(value: unknown): value is DriverInput {
  if (!isRecord(value) || !finite(value.throttle) || !finite(value.brake) || !finite(value.steer) || typeof value.drift !== "boolean") return false;
  return value.useSeq === undefined || (safeInteger(value.useSeq) && value.useSeq >= 0);
}

/** Parse only battle-prefixed envelopes; race protocol 9 remains untouched. */
export function parseBattleClientMessage(raw: string): BattleClientMessage | null {
  let value: unknown;
  try { value = JSON.parse(raw); } catch { return null; }
  if (!isRecord(value) || typeof value.type !== "string" || !value.type.startsWith("battle-")) return null;
  if (value.type === "battle-overlay-hello") {
    if (!hasOnly(value, ["type", "protocol", "sessionId"]) || !safeInteger(value.protocol) || !stringValue(value.sessionId, BATTLE_SESSION_ID_MAX_LENGTH)) return null;
    return { type: value.type, protocol: value.protocol, sessionId: value.sessionId };
  }
  if (value.type === "battle-hello") {
    if (!hasOnly(value, ["type", "protocol", "action", "sessionId", "participantId", "reconnectToken", "arenaId", "seed", "rules", "loadout"])) return null;
    if (!safeInteger(value.protocol) || !["create", "join", "reconnect"].includes(String(value.action))) return null;
    const action = value.action as BattleHelloAction;
    if (action === "create" && (value.sessionId !== undefined || value.participantId !== undefined || value.reconnectToken !== undefined)) return null;
    if (action === "join" && !stringValue(value.sessionId)) return null;
    if (action === "reconnect" && (!stringValue(value.sessionId) || !stringValue(value.participantId) || !stringValue(value.reconnectToken, 512))) return null;
    if (value.arenaId !== undefined && !stringValue(value.arenaId, 80)) return null;
    if (value.seed !== undefined && (!safeInteger(value.seed) || value.seed < 0)) return null;
    if (value.rules !== undefined && !objectValue(value.rules)) return null;
    if (value.loadout !== undefined && !objectValue(value.loadout)) return null;
    return {
      type: "battle-hello", protocol: value.protocol, action,
      ...(value.sessionId !== undefined ? { sessionId: String(value.sessionId) } : {}),
      ...(value.participantId !== undefined ? { participantId: String(value.participantId) } : {}),
      ...(value.reconnectToken !== undefined ? { reconnectToken: String(value.reconnectToken) } : {}),
      ...(value.arenaId !== undefined ? { arenaId: String(value.arenaId) } : {}),
      ...(value.seed !== undefined ? { seed: Number(value.seed) } : {}),
      ...(value.rules !== undefined ? { rules: value.rules as BattleRulesPatch } : {}),
      ...(value.loadout !== undefined ? { loadout: value.loadout as Partial<KartLoadout> } : {}),
    } as BattleClientMessage;
  }
  if (value.type === "battle-ready") return hasOnly(value, ["type", "ready"]) && typeof value.ready === "boolean" ? { type: value.type, ready: value.ready } : null;
  if (value.type === "battle-load-ready") return hasOnly(value, ["type", "matchId"]) && stringValue(value.matchId, 160) ? { type: value.type, matchId: value.matchId } : null;
  if (value.type === "battle-configure") return hasOnly(value, ["type", "patch"]) && objectValue(value.patch) ? { type: value.type, patch: value.patch as BattleRulesPatch } : null;
  if (value.type === "battle-start") return hasOnly(value, ["type"]) ? { type: value.type } : null;
  if (value.type === "battle-rematch") return hasOnly(value, ["type"]) ? { type: value.type } : null;
  if (value.type === "battle-autopilot") return hasOnly(value, ["type", "enabled"]) && typeof value.enabled === "boolean" ? { type: value.type, enabled: value.enabled } : null;
  if (value.type === "battle-leave") return hasOnly(value, ["type"]) ? { type: value.type } : null;
  if (value.type === "battle-lobby") return hasOnly(value, ["type"]) ? { type: value.type } : null;
  if (value.type === "battle-reset") return hasOnly(value, ["type", "matchId"]) && stringValue(value.matchId, 160) ? { type: value.type, matchId: value.matchId } : null;
  if (value.type === "battle-input") {
    if (!hasOnly(value, ["type", "matchId", "seq", "targetTick", "input"]) || !stringValue(value.matchId, 160)
      || !safeInteger(value.seq) || value.seq < 0 || !safeInteger(value.targetTick) || value.targetTick < 0 || !validInput(value.input)) return null;
    const input = value.input;
    return { type: value.type, matchId: value.matchId, seq: value.seq, targetTick: value.targetTick,
      input: { throttle: Math.max(0, Math.min(1, input.throttle)), brake: Math.max(0, Math.min(1, input.brake)), steer: Math.max(-1, Math.min(1, input.steer)), drift: input.drift, useSeq: Math.max(0, Math.floor(input.useSeq ?? 0)) } };
  }
  return null;
}

export function encodeBattleClientMessage(message: BattleClientMessage): string { return JSON.stringify(message); }
export function encodeBattleServerMessage(message: BattleServerMessage): string { return JSON.stringify(message); }

export function parseBattleServerMessage(raw: string): BattleServerMessage | null {
  let value: unknown;
  try { value = JSON.parse(raw); } catch { return null; }
  if (!isRecord(value) || typeof value.type !== "string" || !value.type.startsWith("battle-")) return null;
  if (value.type === "battle-error") {
    return hasOnly(value, ["type", "code", "message", "fatal"]) && stringValue(value.code) && typeof value.message === "string" && typeof value.fatal === "boolean"
      ? { type: value.type, code: value.code, message: value.message, fatal: value.fatal } : null;
  }
  if (value.type === "battle-overlay-welcome") {
    if (!hasOnly(value, ["type", "protocol", "sessionId", "tickRate", "snapshotRate", "serverTick", "snapshot"])) return null;
    if (!safeInteger(value.protocol) || !stringValue(value.sessionId) || !finite(value.tickRate) || !finite(value.snapshotRate)
      || !safeInteger(value.serverTick) || !isRecord(value.snapshot)) return null;
    return value as unknown as BattleServerMessage;
  }
  if (value.type === "battle-overlay-snapshot") {
    if (!hasOnly(value, ["type", "sessionId", "tick", "serverTime", "snapshot"]) || !stringValue(value.sessionId)
      || !safeInteger(value.tick) || !finite(value.serverTime) || !isRecord(value.snapshot)) return null;
    return value as unknown as BattleServerMessage;
  }
  if (value.type === "battle-left") return hasOnly(value, ["type", "sessionId", "participantId"]) && stringValue(value.sessionId) && stringValue(value.participantId) ? { type: value.type, sessionId: value.sessionId, participantId: value.participantId } : null;
  // Battle state is normalized by the authoritative server and is treated as
  // a detached data envelope here; the renderer remains the owner of visuals.
  if (value.type === "battle-welcome") {
    if (!hasOnly(value, ["type", "protocol", "clientId", "sessionId", "participantId", "reconnectToken", "matchId", "phase", "queued", "tickRate", "snapshotRate", "serverTick", "snapshot"])) return null;
    if (!safeInteger(value.protocol) || !stringValue(value.clientId) || !stringValue(value.sessionId) || !stringValue(value.participantId) || !stringValue(value.reconnectToken, 512)
      || (value.matchId !== null && !stringValue(value.matchId)) || !["lobby", "countdown", "active", "results"].includes(String(value.phase))
      || typeof value.queued !== "boolean" || !finite(value.tickRate) || !finite(value.snapshotRate) || !safeInteger(value.serverTick) || !isRecord(value.snapshot)) return null;
    return value as unknown as BattleServerMessage;
  }
  if (value.type === "battle-snapshot") {
    if (!hasOnly(value, ["type", "tick", "serverTime", "snapshot"]) || !safeInteger(value.tick) || !finite(value.serverTime) || !isRecord(value.snapshot)) return null;
    return value as unknown as BattleServerMessage;
  }
  if (value.type === "battle-event") return hasOnly(value, ["type", "sessionId", "matchId", "event"]) && stringValue(value.sessionId) && (value.matchId === null || stringValue(value.matchId)) && isRecord(value.event) ? value as unknown as BattleServerMessage : null;
  if (value.type === "battle-ack") return hasOnly(value, ["type", "action", "snapshot"]) && stringValue(value.action) && isRecord(value.snapshot) ? value as unknown as BattleServerMessage : null;
  return null;
}

export type BattleOverlayServerMessage = Extract<BattleServerMessage, { type: "battle-overlay-welcome" | "battle-overlay-snapshot" }>;

/** Parse only the two read-only overlay envelopes. */
export function parseBattleOverlayServerMessage(raw: string): BattleOverlayServerMessage | null {
  const message = parseBattleServerMessage(raw);
  return message && (message.type === "battle-overlay-welcome" || message.type === "battle-overlay-snapshot") ? message : null;
}
